import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, InputNumber, Modal, Select, Typography } from 'antd';
import { useEffect, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '../../api/client';
import { shipmentsApi } from '../../api/endpoints';
import { invalidateMoneyData } from '../../api/queryKeys';
import type { Shipment, ShipmentItem } from '../../api/types';
import { useStoreContext } from '../../context/StoreContext';
import { ModalSelect } from '../../components/ModalSelect';
import { formatQuantityMilli } from '../../lib/quantity';

/** Modal to transfer a quantity of a line to another store, or return it to the
 * sklad. Quantity is entered in units; the API works in milli-units. */
export function MoveItemModal({
  shipment,
  item,
  mode,
  onBeforeMove,
  onClose,
}: {
  shipment: Shipment;
  item: ShipmentItem | null;
  mode: 'transfer' | 'return';
  /** Runs before the move (e.g. commit unsaved prices); a throw aborts the move. */
  onBeforeMove?: () => Promise<void>;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { stores } = useStoreContext();

  const maxUnits = item ? item.activeQuantityMilli / 1000 : 0;
  const [units, setUnits] = useState<number | null>(null);
  const [toStoreId, setToStoreId] = useState<string | undefined>();
  const [targetShipmentId, setTargetShipmentId] = useState<string | undefined>();

  // Reset the form each time a new item opens.
  useEffect(() => {
    if (item) {
      setUnits(item.activeQuantityMilli / 1000);
      setToStoreId(undefined);
      setTargetShipmentId(undefined);
    }
  }, [item]);

  const candidatesQuery = useQuery({
    queryKey: ['shipments', 'destination-candidates', toStoreId ?? '', shipment.msDate],
    queryFn: () => shipmentsApi.destinationCandidates(toStoreId!, shipment.msDate),
    enabled: mode === 'transfer' && !!toStoreId,
  });
  const candidates = candidatesQuery.data ?? [];

  const onDone = (): void => {
    invalidateMoneyData(queryClient);
    void queryClient.invalidateQueries({ queryKey: ['shipments'] });
    void queryClient.invalidateQueries({ queryKey: ['movements'] });
    onClose();
  };
  const onError = (error: Error): void => {
    message.error(apiErrorMessage(error) ?? t('common.requestError'));
  };

  const mutation = useMutation({
    mutationFn: async () => {
      if (!item || units == null) return;
      await onBeforeMove?.();
      const quantityMilli = Math.round(units * 1000);
      if (mode === 'return') {
        return shipmentsApi.returnItem(shipment.id, item.id, { quantityMilli });
      }
      const result = await shipmentsApi.transferItem(shipment.id, item.id, {
        quantityMilli,
        toStoreId: toStoreId!,
        targetShipmentId,
        // No same-date document → create one (the owner confirmed by pressing OK).
        confirmCreate: candidates.length === 0,
      });
      if ('needsNewShipment' in result) {
        await shipmentsApi.transferItem(shipment.id, item.id, {
          quantityMilli,
          toStoreId: toStoreId!,
          confirmCreate: true,
        });
      }
    },
    onSuccess: (result) => {
      message.success(t(mode === 'return' ? 'shipments.returnDone' : 'shipments.transferDone'));
      // The local return stands even when the MoySklad draft could not be created.
      if (result && 'warnings' in result && result.warnings?.includes('MS_RETURN_SYNC_FAILED')) {
        message.warning(t('shipments.msReturnFailedToast'), 8);
      }
      onDone();
    },
    onError,
  });

  const qtyValid = units != null && units > 0 && units <= maxUnits + 1e-9;
  const canSubmit = qtyValid && (mode === 'return' || !!toStoreId);

  return (
    <Modal
      open={item != null}
      onCancel={onClose}
      onOk={() => mutation.mutate()}
      okButtonProps={{ disabled: !canSubmit, loading: mutation.isPending }}
      okText={t(mode === 'return' ? 'shipments.returnBtn' : 'shipments.transferBtn')}
      cancelText={t('common.cancel')}
      title={t(mode === 'return' ? 'shipments.returnTitle' : 'shipments.transferTitle', {
        name: item?.name ?? '',
      })}
      destroyOnClose
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <div>
          <Typography.Text type="secondary">
            {t('shipments.moveAvailable', {
              qty: item ? formatQuantityMilli(item.activeQuantityMilli) : '',
            })}
          </Typography.Text>
          <InputNumber
            style={{ width: '100%' }}
            value={units}
            onChange={setUnits}
            min={0}
            max={maxUnits}
            step={1}
            precision={3}
            autoFocus
          />
        </div>

        {mode === 'transfer' ? (
          <>
            <div>
              <Typography.Text>{t('shipments.toStore')}</Typography.Text>
              <ModalSelect
                size="middle"
                style={{ width: '100%' }}
                value={toStoreId}
                onChange={(v) => {
                  setToStoreId(v);
                  setTargetShipmentId(undefined);
                }}
                placeholder={t('quick.storePlaceholder')}
                title={t('shipments.toStore')}
                options={stores
                  .filter((s) => s.id !== shipment.storeId)
                  .map((s) => ({ value: s.id, label: s.name }))}
              />
            </div>
            {toStoreId && candidates.length > 0 ? (
              <div>
                <Typography.Text>{t('shipments.toDoc')}</Typography.Text>
                <Select
                  style={{ width: '100%' }}
                  value={targetShipmentId ?? candidates[0]?.id}
                  onChange={setTargetShipmentId}
                  options={candidates.map((c) => ({
                    value: c.id,
                    label: `№${c.msName} · ${t(`shipments.status.${c.status}`)}`,
                  }))}
                />
              </div>
            ) : null}
            {toStoreId && !candidatesQuery.isLoading && candidates.length === 0 ? (
              <Alert type="info" showIcon message={t('shipments.noSameDateDoc')} />
            ) : null}
          </>
        ) : null}
      </div>
    </Modal>
  );
}
